/** Tab-level login session bookkeeping. No imports on purpose: Cookies.jsx and UserInfo.jsx both
 * use it, so it must not depend on either of them.
 *
 * - `active`: this tab had an authenticated session (set when login cookies are written or the
 *   token was validated). Lets a 401 on an anonymous page (protected endpoint called by mistake)
 *   be told apart from a real session expiry.
 * - expiry timestamp: the login cookies' lifetime (30 min, 1 day with Remember me) as epoch ms,
 *   in localStorage, because a cookie's own expiry cannot be read from JavaScript.
 */
const EXPIRY_KEY = 'session-expires-at';
const REASON_KEY = 'session-ended-reason';

let active = false;

export const markSessionActive = () => { active = true; };
export const isSessionActive = () => active;

/** Always overwrites the stored expiry (a new login / token write starts a fresh lifetime) and
 * drops the previous session's end reason.
 * @param {number} minutes - login cookie lifetime */
export const storeSessionExpiry = (minutes) => {
    try{
        window.localStorage.removeItem(REASON_KEY);
        window.localStorage.setItem(EXPIRY_KEY, String(Date.now() + minutes * 60 * 1000));
    }catch{ /* storage blocked: the cookie check still covers expiry */ }
};

/** @returns {number|null} epoch ms the session expires, or null when unknown */
export const getSessionExpiry = () => {
    try{
        const value = Number(window.localStorage.getItem(EXPIRY_KEY));
        return Number.isFinite(value) && value > 0 ? value : null;
    }catch{
        return null;
    }
};

/** @returns {'logout'|'expired'|null} why the last session ended (read by other tabs) */
export const getSessionEndReason = () => {
    try{
        const value = window.localStorage.getItem(REASON_KEY);
        return value === 'logout' || value === 'expired' ? value : null;
    }catch{
        return null;
    }
};

/** Forget the session (explicit logout, expiry).
 * @param {'logout'|'expired'|null} [reason] - recorded for other tabs, written BEFORE the expiry is
 *   removed so a tab reacting to that removal already sees it. null leaves the marker untouched.
 */
export const clearSessionState = (reason = 'logout') => {
    active = false;
    try{
        if(reason) window.localStorage.setItem(REASON_KEY, reason);
        window.localStorage.removeItem(EXPIRY_KEY);
    }catch{ /* ignore */ }
};
